import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { Settings, ShieldAlert, Cpu, RefreshCw, ToggleLeft, ToggleRight, Key, Info, Download } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';

interface Mt5AccountRow {
    id: string;
    user_id: string;
    api_key: string;
    is_active: boolean;
    is_enabled: boolean;
    risk_type: string;
    risk_value: number;
    min_lot: number;
    max_lot: number;
    tick_size: number;
    tick_value: number;
    account_currency: string;
    scanner_313_enabled: boolean;
    scanner_elite_enabled: boolean;
    scanner_admin_enabled: boolean;
    blocked_strategies: string[];
    max_retries: number;
}

const DEFAULT_ACCOUNT: Omit<Mt5AccountRow, 'id' | 'user_id' | 'api_key'> = {
    is_active: false,
    is_enabled: true,
    risk_type: 'equity_pct',
    risk_value: 1.0,
    min_lot: 0.01,
    max_lot: 5.0,
    tick_size: 0.1,
    tick_value: 1.0,
    account_currency: 'USD',
    scanner_313_enabled: true,
    scanner_elite_enabled: false,
    scanner_admin_enabled: true,
    blocked_strategies: [],
    max_retries: 3,
};

export const TradeSettings: React.FC<{ session: Session }> = ({ session }) => {
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [status, setStatus]     = useState('');
    const [account, setAccount]   = useState<Mt5AccountRow | null>(null);
    const [form, setForm]         = useState<typeof DEFAULT_ACCOUNT>({ ...DEFAULT_ACCOUNT });
    const [showApiKey, setShowApiKey] = useState(false);
    const [regenLoading, setRegenLoading] = useState(false);

    useEffect(() => { fetchAccount(); }, []);

    const fetchAccount = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('mt5_accounts')
            .select('*')
            .eq('user_id', session.user.id)
            .maybeSingle();

        if (data) {
            setAccount(data as Mt5AccountRow);
            setForm({
                is_active: data.is_active,
                is_enabled: data.is_enabled,
                risk_type: data.risk_type,
                risk_value: data.risk_value,
                min_lot: data.min_lot,
                max_lot: data.max_lot,
                tick_size: data.tick_size,
                tick_value: data.tick_value,
                account_currency: data.account_currency,
                scanner_313_enabled: data.scanner_313_enabled,
                scanner_elite_enabled: data.scanner_elite_enabled,
                scanner_admin_enabled: data.scanner_admin_enabled,
                blocked_strategies: data.blocked_strategies ?? [],
                max_retries: data.max_retries,
            });
        }
        setLoading(false);
    };

    const saveSettings = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setStatus('');

        const riskVal = parseFloat(form.risk_value.toString());
        if (isNaN(riskVal) || riskVal <= 0) {
            setStatus('Error: Risk value must be a positive number.');
            setSaving(false);
            return;
        }

        const { error } = await supabase.from('mt5_accounts').upsert({
            user_id: session.user.id,
            ...form,
            risk_value: riskVal,
        }, { onConflict: 'user_id' });

        if (error) {
            setStatus('Error: ' + error.message);
        } else {
            setStatus('Settings saved successfully!');
            fetchAccount(); // refresh to get api_key if newly created
        }
        setSaving(false);
    };

    const regenerateApiKey = async () => {
        if (!account?.id) return;
        if (!confirm('Regenerate API key? Your EA will stop working until you update it with the new key.')) return;
        setRegenLoading(true);
        const { data, error } = await supabase.rpc('regenerate_mt5_api_key', {
            p_account_id: account.id,
            p_user_id: session.user.id,
        });
        if (error) {
            setStatus('Error: ' + error.message);
        } else {
            setStatus('API key regenerated. Copy it now — it will be hidden again.');
            fetchAccount();
            setShowApiKey(true);
        }
        setRegenLoading(false);
    };

    const getEndpointUrl = () => {
        const url = import.meta.env.VITE_SUPABASE_URL || '';
        return url ? url.replace('supabase.co', 'supabase.co/functions/v1/mt5-trades') : '<YOUR_SUPABASE_URL>/functions/v1/mt5-trades';
    };

    if (loading) return <div className="text-white text-center mt-20">Loading Settings...</div>;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
                    <Settings className="w-8 h-8 text-finance-accent" />
                    Auto-Trading Settings
                </h1>
                <p className="text-gray-400">Configure your MT5 Expert Advisor connection and risk parameters. The EA polls this platform for pending trades and executes them on your terminal.</p>
            </div>

            <form onSubmit={saveSettings} className="space-y-6">

                {/* ── Master Toggle ─────────────────────────────────────────── */}
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        {form.is_enabled ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                        Master Enable / Disable
                    </h2>
                    <div className="flex items-center gap-6 flex-wrap">
                        <label className="flex items-center gap-3 cursor-pointer group">
                            <div
                                onClick={() => setForm({ ...form, is_enabled: !form.is_enabled })}
                                className={`relative w-12 h-6 rounded-full transition-colors ${form.is_enabled ? 'bg-emerald-500' : 'bg-gray-700'}`}
                            >
                                <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${form.is_enabled ? 'translate-x-6' : ''}`} />
                            </div>
                            <span className="text-white font-medium">{form.is_enabled ? 'Robot Active' : 'Robot Paused'}</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer group">
                            <div
                                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                                className={`relative w-12 h-6 rounded-full transition-colors ${form.is_active ? 'bg-blue-500' : 'bg-gray-700'}`}
                            >
                                <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : ''}`} />
                            </div>
                            <span className="text-white font-medium">{form.is_active ? 'Account Connected' : 'Account Disconnected'}</span>
                        </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                        <strong className="text-yellow-400">Enabled</strong> = robot receives signals. &nbsp;
                        <strong className="text-blue-400">Connected</strong> = EA is running and polling.
                        Disable either to stop trading instantly.
                    </p>
                </div>

                {/* ── API Key & Endpoint ────────────────────────────────────── */}
                {account && (
                    <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <Key className="w-5 h-5 text-yellow-400" />
                            EA Connection Details
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">API Endpoint (set this in your EA)</label>
                                <div className="flex items-center gap-2">
                                    <input readOnly value={getEndpointUrl()} className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-gray-300 text-sm font-mono" />
                                    <button type="button" onClick={() => { navigator.clipboard.writeText(getEndpointUrl()); setStatus('Endpoint copied!'); }}
                                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors">Copy</button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Your API Key (EA uses this as x-api-key header)</label>
                                <div className="flex items-center gap-2">
                                    <input readOnly value={showApiKey ? account.api_key : '••••••••-••••-••••-••••-••••••••••••'}
                                        className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-gray-300 text-sm font-mono" />
                                    <button type="button" onClick={() => setShowApiKey(!showApiKey)}
                                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors">
                                        {showApiKey ? 'Hide' : 'Show'}
                                    </button>
                                    {showApiKey && (
                                        <button type="button" onClick={() => { navigator.clipboard.writeText(account.api_key); setStatus('API key copied!'); }}
                                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors">Copy</button>
                                    )}
                                </div>
                            </div>
                            <button type="button" onClick={regenerateApiKey} disabled={regenLoading}
                                className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50">
                                <RefreshCw className={`w-4 h-4 ${regenLoading ? 'animate-spin' : ''}`} />
                                Regenerate API Key (invalidates current key)
                            </button>
                        </div>
                    </div>
                )}

                {!account && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-blue-300">Save your settings first to generate an API key for your MT5 Expert Advisor.</p>
                    </div>
                )}

                {/* ── Download EA & Setup ───────────────────────────────────── */}
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                        <Download className="w-5 h-5 text-emerald-400" />
                        MT5 Expert Advisor
                    </h2>
                    <p className="text-gray-400 text-sm mb-4">The EA is pre-configured with this platform's URL. You only need to paste <strong className="text-white">your API Key</strong> — that's it.</p>
                    <a
                        href="/ForexFund_AutoTrader.mq5"
                        download="ForexFund_AutoTrader.mq5"
                        className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-lg transition-colors mb-5"
                    >
                        <Download className="w-4 h-4" />
                        Download ForexFund EA (.mq5)
                    </a>
                    <div className="space-y-3">
                        {[
                            { n: '1', t: 'Install', d: 'Copy .mq5 to MT5: File → Open Data Folder → MQL5 → Experts. Open MetaEditor and press F7 to compile.' },
                            { n: '2', t: 'Allow WebRequests', d: 'MT5 → Tools → Options → Expert Advisors → tick Allow WebRequests → add URL: https://zhyekkoguoxhvryucmwk.supabase.co' },
                            { n: '3', t: 'Enter API Key', d: 'Attach the EA to a XAUUSD H1 chart → right-click EA → Properties → Inputs → paste your ApiKey from the "EA Connection Details" section above.' },
                        ].map(({ n, t, d }) => (
                            <div key={n} className="flex gap-3 items-start">
                                <div className="w-6 h-6 rounded-full bg-finance-accent text-finance-darker font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">{n}</div>
                                <div>
                                    <div className="text-white font-medium text-sm">{t}</div>
                                    <div className="text-gray-400 text-xs mt-0.5">{d}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Risk Management ───────────────────────────────────────── */}
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-red-400" />
                        Risk Management
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Risk Type</label>
                            <select value={form.risk_type} onChange={(e) => setForm({ ...form, risk_type: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white">
                                <option value="equity_pct">% of Account Equity</option>
                                <option value="fixed_usd">Fixed USD per Trade</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">
                                Risk Amount {form.risk_type === 'fixed_usd' ? '($)' : '(%)'}
                            </label>
                            <input type="number" step="0.01" min="0.01"
                                value={form.risk_value}
                                onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (!isNaN(v)) setForm({ ...form, risk_value: v });
                                }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                            <p className="text-xs text-gray-500 mt-1">
                                {form.risk_type === 'equity_pct'
                                    ? `${form.risk_value}% of your account equity risked per trade (e.g. $500 equity × ${form.risk_value}% = $${(500 * form.risk_value / 100).toFixed(2)} risk)`
                                    : `Fixed $${form.risk_value} risked on each trade regardless of account size`}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Min Lot Size</label>
                            <input type="number" step="0.01" min="0.01" value={form.min_lot}
                                onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setForm({ ...form, min_lot: v }); }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Max Lot Size</label>
                            <input type="number" step="0.01" min="0.01" value={form.max_lot}
                                onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) setForm({ ...form, max_lot: v }); }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-700 pt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Tick Size <span className="text-gray-500">(default 0.1)</span></label>
                            <input type="number" step="any" min="0" value={form.tick_size}
                                onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setForm({ ...form, tick_size: v }); }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                            <p className="text-xs text-gray-500 mt-1">Check your broker's contract spec for XAUUSD</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Tick Value $ <span className="text-gray-500">(default $1)</span></label>
                            <input type="number" step="any" min="0" value={form.tick_value}
                                onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setForm({ ...form, tick_value: v }); }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                            <p className="text-xs text-gray-500 mt-1">Dollar value per tick per lot from broker spec</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Account Currency</label>
                            <select value={form.account_currency} onChange={(e) => setForm({ ...form, account_currency: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white">
                                <option>USD</option><option>EUR</option><option>GBP</option><option>JPY</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-3 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
                        <strong>Lot size formula:</strong> EA calculates — risk_amount / (abs(entry − SL) / tick_size × tick_value), clamped to [min_lot, max_lot].
                        <br />Example: $50 risk, entry 2050 SL 2040 → SL ticks = 1000, value/lot = $1000, lots = 0.05
                    </div>
                </div>

                {/* ── Scanner Permissions ───────────────────────────────────── */}
                <div className="bg-finance-card border border-gray-800 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <Cpu className="w-5 h-5 text-emerald-400" />
                        Signal Pipeline Permissions
                    </h2>
                    <div className="space-y-4">
                        {[
                            { key: 'scanner_313_enabled', label: 'Standard 313 Strategies', desc: 'Execute signals from the 313-strategy quantitative scanner.' },
                            { key: 'scanner_elite_enabled', label: 'Elite 53 Strategies (Elite plan required)', desc: 'Execute signals from the high-precision Elite scanner. Requires Elite subscription.' },
                            { key: 'scanner_admin_enabled', label: 'Admin / Manual Signals', desc: 'Execute manually created signals from the admin team.' },
                        ].map(({ key, label, desc }) => (
                            <div key={key} className="flex items-start gap-4 p-3 bg-gray-900/50 rounded-lg border border-gray-700/50">
                                <div
                                    onClick={() => setForm({ ...form, [key]: !(form as any)[key] })}
                                    className={`mt-0.5 relative w-10 h-5 rounded-full cursor-pointer transition-colors shrink-0 ${(form as any)[key] ? 'bg-emerald-500' : 'bg-gray-700'}`}
                                >
                                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${(form as any)[key] ? 'translate-x-5' : ''}`} />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-white">{label}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Max Retries per Trade (before marking failed)</label>
                        <select value={form.max_retries} onChange={(e) => setForm({ ...form, max_retries: parseInt(e.target.value) })}
                            className="w-48 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white">
                            <option value={1}>1</option><option value={2}>2</option>
                            <option value={3}>3 (default)</option><option value={5}>5</option>
                        </select>
                    </div>
                </div>

                {/* ── Save Button ───────────────────────────────────────────── */}
                <div className="flex items-center gap-4">
                    <button type="submit" disabled={saving}
                        className="bg-finance-accent hover:bg-yellow-500 text-finance-darker font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Auto-Trading Settings'}
                    </button>
                    {status && (
                        <span className={status.startsWith('Error') ? 'text-red-400 text-sm' : 'text-emerald-400 text-sm'}>{status}</span>
                    )}
                </div>
            </form>
        </div>
    );
};
